/**
 * CallQualitySurvey: pesquisa opcional ao fim da chamada: qualidade do áudio e do vídeo, de 1 a 5.
 *
 * Basta uma das duas notas para enviar. "Fechar" pula a pesquisa sem enviar nada (a chamada já
 * entra na contabilização pelo backend). Com a resposta vai o tempo que cada lado passou em cada
 * nível de conexão do LiveKit (`getConnectionQuality`). Depois de enviar, agradece e fecha.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useSubmitQualitySurvey } from '@/features/otto/api/useVideoCallApi';
import { StarRating } from './StarRating';

/** Tempo do agradecimento antes de fechar a janela. */
export const THANKS_CLOSE_MS = 2000;

/**
 * @param {{ videoCallId: string, getConnectionQuality?: () => object|null, onDone: () => void }} props
 */
export function CallQualitySurvey({ videoCallId, getConnectionQuality, onDone }) {
    const [audioRating, setAudioRating] = useState(null);
    const [videoRating, setVideoRating] = useState(null);
    const [sent, setSent] = useState(false);
    const mutation = useSubmitQualitySurvey();

    useEffect(() => {
        if (!sent) return undefined;
        const id = setTimeout(onDone, THANKS_CLOSE_MS);
        return () => clearTimeout(id);
    }, [sent, onDone]);

    const submit = async () => {
        try {
            await mutation.mutateAsync({
                videoCallId,
                audioRating,
                videoRating,
                connectionQuality: getConnectionQuality?.() ?? null,
            });
            setSent(true);
        } catch (err) {
            // Já avaliada (outra aba, clique duplo): para o atendente é o mesmo que ter enviado.
            if (err?.code === 'SURVEY_ALREADY_ANSWERED') setSent(true);
            else toast.error('Não foi possível enviar a avaliação');
        }
    };

    if (sent) {
        return (
            <div className="flex flex-col items-center gap-2" role="status">
                <p className="text-sm font-medium text-foreground">Obrigado pela avaliação</p>
                <Button size="sm" variant="outline" onClick={onDone}>Fechar</Button>
            </div>
        );
    }

    const nothingRated = audioRating == null && videoRating == null;
    return (
        <section aria-label="Avalie a chamada" className="flex w-full flex-col items-center gap-2">
            <div className="text-center">
                <p className="text-sm font-medium text-foreground">Avalie a chamada</p>
                <p className="text-xs text-foreground-muted">Opcional</p>
            </div>
            <StarRating label="Como foi a qualidade do áudio?" value={audioRating} onChange={setAudioRating} disabled={mutation.isPending} />
            <StarRating label="Como foi a qualidade do vídeo?" value={videoRating} onChange={setVideoRating} disabled={mutation.isPending} />
            <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={onDone} disabled={mutation.isPending}>Fechar</Button>
                <Button size="sm" onClick={submit} disabled={nothingRated || mutation.isPending}>Enviar avaliação</Button>
            </div>
        </section>
    );
}

export default CallQualitySurvey;
